import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import copy from 'copy-to-clipboard';
import clsx from 'clsx';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { TypedMemo } from '../../common/types';
import { splitShareUrl } from './publishedPageFacts';
import styles from './SMLPublishedAddress.module.scss';
import { trackEvent } from '../../analytics/sml/analytics';

interface SMLPublishedAddressProps {
  shareUrl: string;
}

const COPIED_MS = 1600;

/**
 * The address, first and largest.
 *
 * It is the one thing the artist came for: the string they are about to paste
 * into a bio, a story or a group chat. The host is greyed so the part that
 * changes — the slug they chose — is the part the eye lands on.
 */
const SMLPublishedAddressComponent: React.FC<SMLPublishedAddressProps> = ({ shareUrl }) => {
  const intl = useIntl();
  const [isCopied, setIsCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { host, slug } = splitShareUrl(shareUrl);

  useEffect(
    () => () => {
      if (timer.current !== null) {
        clearTimeout(timer.current);
      }
    },
    []
  );

  const onCopy = useCallback(() => {
    copy(shareUrl);
    setIsCopied(true);
    trackEvent('link_copied', { kind: 'smartlink' });
    if (timer.current !== null) {
      clearTimeout(timer.current);
    }
    timer.current = setTimeout(() => setIsCopied(false), COPIED_MS);
  }, [shareUrl]);

  return (
    <div className={styles.row}>
      <span className={styles.address} title={shareUrl}>
        <span className={styles.host}>{host}</span>
        <span className={styles.slug}>{slug}</span>
      </span>
      <button type="button" className={clsx(styles.copy, isCopied && styles.copied)} onClick={onCopy}>
        <FontAwesomeIcon icon={isCopied ? faCheck : faCopy} />
        {isCopied
          ? intl.formatMessage({ id: 'app.sml.published.copied', defaultMessage: 'Copied' })
          : intl.formatMessage({ id: 'app.sml.published.copy', defaultMessage: 'Copy' })}
      </button>
    </div>
  );
};

export const SMLPublishedAddress = TypedMemo(SMLPublishedAddressComponent);
